import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Save, FileSpreadsheet } from "lucide-react";
import { API } from "../../lib/api";
import { exportRowsToXlsx } from "../../lib/exportToXlsx";

const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const CATEGORIES = ["Professional Charges", "Admin", "Rent", "Utilities", "Travel", "Software", "Marketing", "Other"];

const curFy = () => {
  const t = new Date();
  const sy = t.getMonth() + 1 >= 4 ? t.getFullYear() : t.getFullYear() - 1;
  return `${sy}-${String(sy + 1).slice(2)}`;
};

const fyOptions = (count = 5) => {
  const t = new Date();
  const curStart = t.getMonth() + 1 >= 4 ? t.getFullYear() : t.getFullYear() - 1;
  return Array.from({ length: count }, (_, i) => {
    const y = curStart - i;
    return `${y}-${String(y + 1).slice(2)}`;
  });
};

export default function MIS() {
  const { user } = useAuth();
  const allowed = ["partner", "admin"].includes(user?.role);
  const [periodMode, setPeriodMode] = useState("fy");
  const [fy, setFy] = useState(curFy());
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [customRange, setCustomRange] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ month: new Date().toISOString().slice(0, 7), category: "Professional Charges", amount: 0, notes: "" });
  const [drilldown, setDrilldown] = useState(null);
  const [editRevMonth, setEditRevMonth] = useState(null);
  const [editRevForm, setEditRevForm] = useState({ revenue: 0, note: "" });
  const [expFilter, setExpFilter] = useState({ start: "", end: "", category: "" });
  const [categorySummary, setCategorySummary] = useState(null);

  const periodQuery = () => {
    if (periodMode === "fy") {
      const sy = parseInt(fy.split("-")[0], 10);
      return `fy=${sy}-${sy + 1}`;
    }
    if (periodMode === "custom" && customRange.start && customRange.end) {
      return `start_date=${customRange.start}&end_date=${customRange.end}`;
    }
    return `year=${year}`;
  };

  const expFilterQuery = () => {
    const parts = [];
    if (expFilter.start) parts.push(`start_date=${expFilter.start}`);
    if (expFilter.end) parts.push(`end_date=${expFilter.end}`);
    if (expFilter.category) parts.push(`category=${encodeURIComponent(expFilter.category)}`);
    return parts.join("&");
  };

  const load = () => Promise.all([
    api.get(`/mis?${periodQuery()}`).then((r) => setData(r.data)),
    api.get(`/mis/expenses${expFilterQuery() ? "?" + expFilterQuery() : ""}`).then((r) => setExpenses(r.data)),
    api.get(`/mis/expenses/category-summary${expFilterQuery() ? "?" + expFilterQuery() : ""}`).then((r) => setCategorySummary(r.data)).catch(() => {}),
  ]);
  useEffect(() => { if (allowed) load(); }, [periodMode, fy, year, customRange.start, customRange.end, allowed, expFilter.start, expFilter.end, expFilter.category]);

  const downloadXlsx = () => {
    if (!data) { toast.error("Load a period first"); return; }
    const rows = [...(data.rows || [])];
    rows.push({
      month: "TOTAL",
      revenue: data.yearly.revenue,
      ope: data.yearly.ope || 0,
      total_revenue: data.yearly.total_revenue || data.yearly.revenue,
      employee_cost: data.yearly.employee_cost,
      other_expenses: data.yearly.other_expenses,
      net_pl: data.yearly.net_pl,
    });
    exportRowsToXlsx({
      filename: `mis_${data.period}.xlsx`,
      sheetName: `MIS ${data.period}`,
      columns: [
        { key: "month", label: "Month" },
        { key: "revenue", label: "Revenue (₹)" },
        { key: "ope", label: "OPE (₹)" },
        { key: "total_revenue", label: "Total Revenue (₹)" },
        { key: "employee_cost", label: "Employee Cost (₹)" },
        { key: "other_expenses", label: "Other Expenses (₹)" },
        { key: "net_pl", label: "Net P&L (₹)" },
      ],
      rows,
    });
  };

  if (!allowed) {
    return <div className="text-sm text-slate-500" data-testid="mis-forbidden">Not authorised.</div>;
  }

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/mis/expenses", { ...form, amount: Number(form.amount) });
      toast.success("Expense recorded");
      setOpen(false);
      setForm({ month: new Date().toISOString().slice(0, 7), category: "Professional Charges", amount: 0, notes: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this expense entry?")) return;
    try { await api.delete(`/mis/expenses/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const openDrilldown = async (month, kind) => {
    try {
      const { data: dd } = await api.get(`/mis/drilldown?month=${month}&kind=${kind}`);
      setDrilldown(dd);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openEditRevenue = (row) => {
    setEditRevMonth(row.month);
    setEditRevForm({ revenue: row.revenue, note: row.override_note || "" });
  };

  const saveRevenueOverride = async () => {
    try {
      await api.post(`/billing/monthly-revenue-override`, {
        month: editRevMonth,
        revenue: Number(editRevForm.revenue),
        note: editRevForm.note || "",
      });
      toast.success(`Revenue for ${editRevMonth} overridden`);
      setEditRevMonth(null); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const clearRevenueOverride = async (month) => {
    if (!window.confirm("Remove revenue override for this month?")) return;
    try {
      await api.delete(`/billing/monthly-revenue-override/${month}`);
      toast.success("Override cleared");
      setEditRevMonth(null); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="mis-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">MIS</div>
          <h1 className="font-sans text-4xl mt-2">Revenue & expenses</h1>
          <p className="text-slate-600 text-sm mt-1">Period: <span className="font-medium">{data?.period || "—"}</span> · Click any number to drill down.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={periodMode} onValueChange={setPeriodMode}>
            <SelectTrigger className="rounded-sm w-44" data-testid="mis-period-mode"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="fy">Financial Year</SelectItem>
              <SelectItem value="year">Calendar Year</SelectItem>
              <SelectItem value="custom">Custom Period</SelectItem>
            </SelectContent>
          </Select>
          {periodMode === "fy" && (
            <div>
              <Label className="eyebrow">FY</Label>
              <Select value={fy} onValueChange={setFy}>
                <SelectTrigger className="mt-2 rounded-sm w-32" data-testid="mis-fy"><SelectValue/></SelectTrigger>
                <SelectContent>{fyOptions(5).map((f) => <SelectItem key={f} value={f}>FY {f}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {periodMode === "year" && <div><Label className="eyebrow">Year</Label><Input value={year} onChange={(e) => setYear(e.target.value)} className="rounded-sm w-28 mt-2" data-testid="mis-year"/></div>}
          {periodMode === "custom" && <>
            <div><Label className="eyebrow">From</Label><Input type="date" value={customRange.start} onChange={(e) => setCustomRange({ ...customRange, start: e.target.value })} className="rounded-sm w-40 mt-2" data-testid="mis-start"/></div>
            <div><Label className="eyebrow">To</Label><Input type="date" value={customRange.end} onChange={(e) => setCustomRange({ ...customRange, end: e.target.value })} className="rounded-sm w-40 mt-2" data-testid="mis-end"/></div>
          </>}
          <Button variant="outline" onClick={downloadXlsx} className="rounded-sm" data-testid="mis-export-xlsx"><FileSpreadsheet className="h-4 w-4 mr-2"/>Excel</Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="add-expense-btn"><Plus className="h-4 w-4 mr-2"/>Add Expense</Button></DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle className="font-sans text-xl">Add Expense</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div><Label className="eyebrow">Month</Label><Input type="month" required value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} className="mt-2 rounded-sm" data-testid="exp-month"/></div>
                <div><Label className="eyebrow">Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="mt-2 rounded-sm" data-testid="exp-category"><SelectValue/></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="eyebrow">Amount</Label><Input type="number" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-2 rounded-sm" data-testid="exp-amount"/></div>
                <div><Label className="eyebrow">Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-2 rounded-sm"/></div>
                <DialogFooter><Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="exp-save-btn">Save</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-6 gap-px bg-slate-200 border border-slate-200" data-testid="mis-yearly">
            <div className="bg-white p-5"><div className="eyebrow">Revenue</div><div className="serif-num text-3xl mt-2 text-emerald-700">{fmt(data.yearly.revenue)}</div></div>
            <div className="bg-white p-5"><div className="eyebrow">OPE</div><div className="serif-num text-3xl mt-2 text-teal-700">{fmt(data.yearly.ope || 0)}</div></div>
            <div className="bg-white p-5"><div className="eyebrow">Total Revenue</div><div className="serif-num text-3xl mt-2 text-emerald-800">{fmt(data.yearly.total_revenue || data.yearly.revenue)}</div></div>
            <div className="bg-white p-5"><div className="eyebrow">Employee Cost</div><div className="serif-num text-3xl mt-2 text-red-700">{fmt(data.yearly.employee_cost)}</div></div>
            <div className="bg-white p-5"><div className="eyebrow">Other Expenses</div><div className="serif-num text-3xl mt-2 text-orange-700">{fmt(data.yearly.other_expenses)}</div></div>
            <div className="bg-white p-5"><div className="eyebrow">Net P&amp;L</div><div className={`serif-num text-3xl mt-2 ${data.yearly.net_pl >= 0 ? "text-emerald-800" : "text-red-800"}`}>{fmt(data.yearly.net_pl)}</div></div>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
            <div className="p-6 border-b border-slate-200 eyebrow">Monthly P&amp;L · {data.period}</div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
                <tr>
                  <th className="text-left p-4">Month</th>
                  <th className="text-right p-4">Revenue</th>
                  <th className="text-right p-4">OPE</th>
                  <th className="text-right p-4 font-semibold">Total Revenue</th>
                  <th className="text-right p-4">Employee Cost</th>
                  <th className="text-right p-4">Other Expenses</th>
                  <th className="text-right p-4 font-semibold">Net P&amp;L</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-testid="mis-monthly">
                {data.rows.map((r) => (
                  <tr key={r.month} className="hover:bg-slate-50">
                    <td className="p-4 font-mono text-xs">{r.month}</td>
                    <td className="p-4 text-right serif-num text-emerald-700">
                      <button onClick={() => openDrilldown(r.month, "revenue")} className="hover:underline" data-testid={`drill-rev-${r.month}`}>{fmt(r.revenue)}</button>
                      {r.is_override && <span className="ml-2 text-[10px] uppercase tracking-[0.2em] text-amber-700" title={`Auto: ${fmt(r.revenue_auto)} · ${r.override_note || ""}`}>· edited</span>}
                    </td>
                    <td className="p-4 text-right serif-num text-teal-700" data-testid={`ope-${r.month}`}>{fmt(r.ope || 0)}</td>
                    <td className="p-4 text-right serif-num text-emerald-800 font-semibold" data-testid={`totrev-${r.month}`}>{fmt(r.total_revenue ?? r.revenue)}</td>
                    <td className="p-4 text-right serif-num text-red-700"><button onClick={() => openDrilldown(r.month, "employee_cost")} className="hover:underline" data-testid={`drill-emp-${r.month}`}>{fmt(r.employee_cost)}</button></td>
                    <td className="p-4 text-right serif-num text-orange-700"><button onClick={() => openDrilldown(r.month, "other_expenses")} className="hover:underline" data-testid={`drill-exp-${r.month}`}>{fmt(r.other_expenses)}</button></td>
                    <td className={`p-4 text-right serif-num font-semibold ${r.net_pl >= 0 ? "text-emerald-800" : "text-red-800"}`}>{fmt(r.net_pl)}</td>
                    <td className="p-4 text-right">
                      <button onClick={() => openEditRevenue(r)} className="text-slate-400 hover:text-slate-900" title="Override revenue" data-testid={`edit-rev-${r.month}`}><Pencil className="h-3.5 w-3.5"/></button>
                    </td>
                  </tr>
                ))}
                {data.rows.length > 0 && (
                  <tr className="bg-slate-50 font-semibold border-t-2 border-slate-300" data-testid="mis-monthly-totals">
                    <td className="p-4 uppercase tracking-[0.18em] text-xs text-slate-600">Total</td>
                    <td className="p-4 text-right serif-num text-emerald-700">{fmt(data.yearly.revenue)}</td>
                    <td className="p-4 text-right serif-num text-teal-700">{fmt(data.yearly.ope || 0)}</td>
                    <td className="p-4 text-right serif-num text-emerald-800">{fmt(data.yearly.total_revenue || data.yearly.revenue)}</td>
                    <td className="p-4 text-right serif-num text-red-700">{fmt(data.yearly.employee_cost)}</td>
                    <td className="p-4 text-right serif-num text-orange-700">{fmt(data.yearly.other_expenses)}</td>
                    <td className={`p-4 text-right serif-num ${data.yearly.net_pl >= 0 ? "text-emerald-800" : "text-red-800"}`}>{fmt(data.yearly.net_pl)}</td>
                    <td></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {categorySummary && categorySummary.by_category && categorySummary.by_category.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="mis-category-summary">
          <div className="p-6 border-b border-slate-200 flex items-center justify-between">
            <div className="eyebrow">Expenses · Category Summary</div>
            <div className="text-xs text-slate-500">Period: {categorySummary.period?.fy ? `FY ${categorySummary.period.fy}` : categorySummary.period?.start ? `${categorySummary.period.start} → ${categorySummary.period.end}` : "All"}</div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
              <tr><th className="text-left p-4">Category</th><th className="text-right p-4">Entries</th><th className="text-right p-4">Amount</th><th className="text-right p-4">% of Total</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-testid="mis-category-table">
              {categorySummary.by_category.map((c) => (
                <tr key={c.category} className="hover:bg-slate-50">
                  <td className="p-4 font-medium">{c.category}</td>
                  <td className="p-4 text-right serif-num">{c.count}</td>
                  <td className="p-4 text-right serif-num">{fmt(c.amount)}</td>
                  <td className="p-4 text-right serif-num text-slate-500">{categorySummary.total > 0 ? `${((c.amount / categorySummary.total) * 100).toFixed(1)}%` : "—"}</td>
                </tr>
              ))}
              <tr className="bg-slate-50 font-semibold border-t-2 border-slate-300" data-testid="mis-category-totals">
                <td className="p-4 uppercase tracking-[0.18em] text-xs text-slate-600">Total ({categorySummary.count})</td>
                <td className="p-4 text-right serif-num">{categorySummary.count}</td>
                <td className="p-4 text-right serif-num">{fmt(categorySummary.total)}</td>
                <td className="p-4 text-right serif-num">100%</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <div className="p-6 border-b border-slate-200 flex flex-wrap items-end justify-between gap-3">
          <div className="eyebrow">Expense Entries</div>
          <div className="flex items-end gap-2 flex-wrap">
            <div><Label className="text-xs">From</Label><Input type="date" value={expFilter.start} onChange={(e) => setExpFilter({ ...expFilter, start: e.target.value })} className="rounded-sm w-36 mt-1" data-testid="exp-filter-start"/></div>
            <div><Label className="text-xs">To</Label><Input type="date" value={expFilter.end} onChange={(e) => setExpFilter({ ...expFilter, end: e.target.value })} className="rounded-sm w-36 mt-1" data-testid="exp-filter-end"/></div>
            <div><Label className="text-xs">Category</Label>
              <Select value={expFilter.category || "_all"} onValueChange={(v) => setExpFilter({ ...expFilter, category: v === "_all" ? "" : v })}>
                <SelectTrigger className="rounded-sm w-48 mt-1" data-testid="exp-filter-category"><SelectValue placeholder="All categories"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">All categories</SelectItem>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {(expFilter.start || expFilter.end || expFilter.category) && (
              <Button variant="outline" size="sm" onClick={() => setExpFilter({ start: "", end: "", category: "" })} className="rounded-sm" data-testid="exp-filter-clear">Clear</Button>
            )}
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
            <tr><th className="text-left p-4">Month</th><th className="text-left p-4">Category</th><th className="text-right p-4">Amount</th><th className="text-left p-4">Notes</th><th className="p-4"></th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-testid="mis-expense-table">
            {expenses.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-500">No expenses logged yet.</td></tr>}
            {expenses.map((e) => (
              <tr key={e.id} className="hover:bg-slate-50">
                <td className="p-4 font-mono text-xs">{e.month}</td>
                <td className="p-4 font-medium">{e.category}</td>
                <td className="p-4 text-right serif-num">{fmt(e.amount)}</td>
                <td className="p-4 text-slate-600">{e.notes || "—"}</td>
                <td className="p-4 text-right"><button onClick={() => remove(e.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4"/></button></td>
              </tr>
            ))}
            {expenses.length > 0 && (
              <tr className="bg-slate-50 font-semibold border-t-2 border-slate-300" data-testid="mis-expense-totals">
                <td colSpan={2} className="p-4 uppercase tracking-[0.18em] text-xs text-slate-600">Sub-total ({expenses.length} entries)</td>
                <td className="p-4 text-right serif-num">{fmt(expenses.reduce((s, e) => s + (Number(e.amount) || 0), 0))}</td>
                <td colSpan={2}></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Drill-down dialog */}
      <Dialog open={!!drilldown} onOpenChange={(v) => !v && setDrilldown(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="mis-drilldown-dialog">
          <DialogHeader>
            <DialogTitle className="font-sans text-2xl">
              {drilldown?.kind === "revenue" ? "Revenue" : drilldown?.kind === "employee_cost" ? "Employee Cost" : "Other Expenses"} · {drilldown?.month}
            </DialogTitle>
          </DialogHeader>
          {drilldown && (
            <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
              <table className="w-full text-sm">
                {drilldown.kind === "revenue" && (
                  <>
                    <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
                      <tr><th className="text-left p-3">Invoice</th><th className="text-left p-3">Date</th><th className="text-left p-3">Client</th><th className="text-right p-3">Amount</th><th className="text-right p-3">Total (incl GST)</th><th className="text-left p-3">Status</th></tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {drilldown.rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-500">No invoices.</td></tr>}
                      {drilldown.rows.map((r, i) => (
                        <tr key={i}><td className="p-3 font-mono text-xs">{r.invoice_number}</td><td className="p-3 font-mono text-xs">{r.invoice_date}</td><td className="p-3">{r.client_name}</td><td className="p-3 text-right serif-num">{fmt(r.amount)}</td><td className="p-3 text-right serif-num">{fmt(r.total_amount)}</td><td className="p-3 text-xs uppercase tracking-[0.15em] text-slate-600">{r.status}</td></tr>
                      ))}
                    </tbody>
                  </>
                )}
                {drilldown.kind === "employee_cost" && (
                  <>
                    <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
                      <tr><th className="text-left p-3">Code</th><th className="text-left p-3">Employee</th><th className="text-right p-3">Gross</th><th className="text-right p-3">Deductions</th><th className="text-right p-3">Net Pay</th><th className="text-left p-3">Status</th></tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {drilldown.rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-500">No payroll for this month.</td></tr>}
                      {drilldown.rows.map((r, i) => (
                        <tr key={i}><td className="p-3 font-mono text-xs">{r.employee_code}</td><td className="p-3">{r.employee_name}</td><td className="p-3 text-right serif-num">{fmt(r.gross)}</td><td className="p-3 text-right serif-num text-red-700">{fmt(r.total_deductions)}</td><td className="p-3 text-right serif-num font-semibold">{fmt(r.net_pay)}</td><td className="p-3 text-xs uppercase tracking-[0.15em] text-slate-600">{r.status}</td></tr>
                      ))}
                    </tbody>
                  </>
                )}
                {drilldown.kind === "other_expenses" && (
                  <>
                    <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
                      <tr><th className="text-left p-3">Category</th><th className="text-right p-3">Amount</th><th className="text-left p-3">Notes</th></tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {drilldown.rows.length === 0 && <tr><td colSpan={3} className="p-6 text-center text-slate-500">No expense entries.</td></tr>}
                      {drilldown.rows.map((r, i) => (
                        <tr key={i}><td className="p-3 font-medium">{r.category}</td><td className="p-3 text-right serif-num">{fmt(r.amount)}</td><td className="p-3 text-slate-600">{r.notes || "—"}</td></tr>
                      ))}
                    </tbody>
                  </>
                )}
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit revenue override dialog */}
      <Dialog open={!!editRevMonth} onOpenChange={(v) => !v && setEditRevMonth(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-sans text-xl">Override Revenue · {editRevMonth}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="eyebrow">Revenue (₹)</Label><Input type="number" step="0.01" value={editRevForm.revenue} onChange={(e) => setEditRevForm({ ...editRevForm, revenue: e.target.value })} className="mt-2 rounded-sm" data-testid="rev-override-amount"/></div>
            <div><Label className="eyebrow">Note (optional)</Label><Textarea rows={2} value={editRevForm.note} onChange={(e) => setEditRevForm({ ...editRevForm, note: e.target.value })} className="mt-2 rounded-sm" data-testid="rev-override-note"/></div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => clearRevenueOverride(editRevMonth)} className="rounded-sm border-red-200 text-red-700" data-testid="rev-override-clear">Clear Override</Button>
            <Button onClick={saveRevenueOverride} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="rev-override-save"><Save className="h-4 w-4 mr-2"/>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
